import { Ref, useImperativeHandle, useMemo, useState } from 'react';
import styled from 'styled-components';

import { MEDIA_QUERIES } from '#/hooks/useResponsiveValue';
import { ButtonVariant } from '#/service/constants';
import { remify } from '#/service/utils';
import { tb } from '#/store/translation/translation.store';

import { Button } from '../Button/Button';
import { Modal } from './Modal';

interface ShowMoreContents {
  title: string;
  text: string;
}

export interface ShowMoreModalApi {
  open: (contents: ShowMoreContents) => void;
  close: () => void;
}

interface ShowMoreModalProps {
  apiRef: Ref<ShowMoreModalApi>;
}

const StyledModal = styled(Modal)`
  & h2 {
    flex-shrink: 0;
    margin: 0;
    text-wrap: balance;
    overflow: hidden;
    font: var(--fontStyleHeadline);
  }

  & .modal-body {
    max-width: min(80dvw, 43rem);
    max-height: 64dvh;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: ${remify(20)};
    white-space: pre-line;
  }

  & .close-button {
    justify-content: center;
    align-self: center;
  }

  @media screen and ${MEDIA_QUERIES.sm} {
    width: 100dvw;
    height: calc(100dvh - var(--menuHeight, 0rem));
    margin-top: var(--menuHeight, 0);
    box-sizing: border-box;

    & .modal-body {
      max-width: unset;
      max-height: unset;
      height: 100%;
    }

    & .close-button {
      align-self: auto;
    }
  }
`;

const TextContainer = styled.div`
  overflow-y: auto;
  font: var(--fontStyleBody);
  text-align: left;
`;

export const ShowMoreModal = ({ apiRef }: ShowMoreModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [contents, setContents] = useState<ShowMoreContents>({
    title: '',
    text: '',
  });

  const api: ShowMoreModalApi = useMemo(
    () => ({
      open: (contents: ShowMoreContents) => {
        setContents(contents);
        setIsOpen(true);
      },
      close: () => {
        setIsOpen(false);
      },
    }),
    []
  );

  // Unlike the regular Modal, this one uses an imperative handle.
  // That means the PageController don't have to manage the modal open state, so the Page won't rerender every time.
  useImperativeHandle(apiRef, () => api);

  return (
    <StyledModal
      closeOnEsc
      closeOnOverlayClick
      isOpen={isOpen}
      onClose={api.close}
      showCloseButton
    >
      <Modal.Title>{contents.title}</Modal.Title>
      <TextContainer>{contents.text}</TextContainer>
      <Button
        className="close-button"
        onClick={api.close}
        variant={ButtonVariant.Primary}
      >
        {tb('button.close')}
      </Button>
    </StyledModal>
  );
};
